import type { TimekeepingPeriod } from 'src/store/selectors';
import type { Hours, Minutes, TimeBalanceAnalysis } from 'src/utilities/time-balance/types';

type TimeBalancePeriod = Pick<
  TimekeepingPeriod,
  'confirmed' | 'type' | 'minutes' | 'start' | 'startTime' | 'end' | 'endTime'
>;

function dateParser<T>(parser: (data: T) => string, data: T): Date | undefined {
  const date = new Date(parser(data));
  if (Number.isNaN(date.getTime())) {
    return undefined;
  } else {
    return date;
  }
}

export function parseEmploymentStartOrEnd(dateString?: string): Date | undefined {
  return dateParser((data) => `${data}T00:00Z`, dateString);
}

function parseEmploymentEnd(dateString?: string): Date | undefined {
  const date = parseEmploymentStartOrEnd(dateString);
  if (date === undefined) {
    return undefined;
  } else {
    return new Date(date.getTime() + 24 * 60 * 60 * 1000 - 1);
  }
}

export function parseTimekeepingPeriodStart(period: Pick<TimekeepingPeriod, 'start' | 'startTime'>): Date | undefined {
  return dateParser((data) => {
    const coercedStartTime = data.startTime.trim() === '' ? '00:00' : data.startTime;
    return `${data.start}T${coercedStartTime}Z`;
  }, period);
}

export function parseTimekeepingPeriodEnd(period: Pick<TimekeepingPeriod, 'end' | 'endTime'>): Date | undefined {
  return dateParser((data) => {
    const coercedEndTime = data.endTime.trim() === '' ? '00:00' : data.endTime;
    return `${data.end}T${coercedEndTime}Z`;
  }, period);
}

export function isAfterEmploymentStart(periodStart: Date, employmentStart: Date): boolean {
  return periodStart.getTime() >= employmentStart.getTime();
}

export function isBeforeEmploymentEnd(periodEnd: Date, employmentEnd: Date): boolean {
  return periodEnd.getTime() < employmentEnd.getTime();
}

export function parseTimekeepingPeriodDuration(
  period: Pick<TimekeepingPeriod, 'type' | 'minutes'>,
): Minutes | undefined {
  const type = period.type;
  if (type === 'plusTime' || type === 'correctionPlus') {
    return period.minutes;
  } else if (type === 'timeOff' || type === 'correction') {
    return -period.minutes;
  } else {
    return undefined;
  }
}

export function sumDurations(periods: Minutes[]): Hours {
  return periods.reduce((acc, curr) => acc + curr, 0) / 60.0;
}

export function calculateBalance(
  periods: TimeBalancePeriod[],
  employmentStartDate?: string,
  employmentEndDate?: string,
): Hours {
  return analyzeTimeBalance(periods, employmentStartDate, employmentEndDate).balance;
}

export function analyzeTimeBalance(
  periods: TimeBalancePeriod[],
  employmentStartDate?: string,
  employmentEndDate?: string,
): TimeBalanceAnalysis {
  const employmentStart = parseEmploymentStartOrEnd(employmentStartDate);
  const employmentEnd = parseEmploymentEnd(employmentEndDate);
  const durations: Minutes[] = [];
  let hasTimekeepingBeforeEmploymentStart = false;
  let hasTimekeepingAfterEmploymentEnd = false;
  let latestValidStartDate: Date | undefined = undefined;
  let earliestValidEndDate: Date | undefined = undefined;

  for (const period of periods) {
    const periodStart = parseTimekeepingPeriodStart(period);
    const periodEnd = parseTimekeepingPeriodEnd(period);

    const beforeEmployment =
      employmentStart !== undefined &&
      periodStart !== undefined &&
      isBeforeEmploymentStart(periodStart, employmentStart);
    hasTimekeepingBeforeEmploymentStart = hasTimekeepingBeforeEmploymentStart || beforeEmployment;
    if (
      beforeEmployment &&
      (latestValidStartDate === undefined || periodStart.getTime() < latestValidStartDate.getTime())
    ) {
      latestValidStartDate = periodStart;
    }

    const afterEmployment =
      employmentEnd !== undefined && periodEnd !== undefined && isAfterEmploymentEnd(periodEnd, employmentEnd);
    hasTimekeepingAfterEmploymentEnd = hasTimekeepingAfterEmploymentEnd || afterEmployment;
    if (
      afterEmployment &&
      (earliestValidEndDate === undefined || periodEnd.getTime() > earliestValidEndDate.getTime())
    ) {
      earliestValidEndDate = periodEnd;
    }

    if (!period.confirmed) continue;
    if (periodStart === undefined) continue;
    if (periodEnd === undefined) continue;
    if (employmentStart !== undefined && isBeforeEmploymentStart(periodStart, employmentStart)) {
      continue;
    }
    if (employmentEnd !== undefined && isAfterEmploymentEnd(periodEnd, employmentEnd)) {
      continue;
    }

    const duration = parseTimekeepingPeriodDuration(period);
    if (duration !== undefined) durations.push(duration);
  }

  const result: TimeBalanceAnalysis = {
    balance: sumDurations(durations),
    hasTimekeepingOutsideEmploymentPeriod: hasTimekeepingBeforeEmploymentStart || hasTimekeepingAfterEmploymentEnd,
  };
  if (latestValidStartDate !== undefined) {
    result.latestValidStartDate = latestValidStartDate;
  }
  if (earliestValidEndDate !== undefined) {
    result.earliestValidEndDate = earliestValidEndDate;
  }
  return result;
}

export function isBeforeEmploymentStart(periodStart: Date, employmentStart: Date) {
  return !isAfterEmploymentStart(periodStart, employmentStart);
}

export function isAfterEmploymentEnd(periodEnd: Date, employmentEnd: Date) {
  return !isBeforeEmploymentEnd(periodEnd, employmentEnd);
}

export function getEarliestTimekeepingPeriod(periods: TimeBalancePeriod[]): TimeBalancePeriod | undefined {
  const validPeriods = periods
    .map((p) => ({ start: parseTimekeepingPeriodStart(p), period: p }))
    .filter(({ start }) => start !== undefined);
  if (validPeriods.length === 0) {
    return undefined;
  } else {
    return validPeriods.reduce((min, curr) => {
      if (min.start!.getTime() < curr.start!.getTime()) {
        return min;
      } else {
        return curr;
      }
    }).period;
  }
}

export function getLatestTimekeepingPeriod(periods: TimeBalancePeriod[]): TimeBalancePeriod | undefined {
  const validPeriods = periods
    .map((p) => ({ end: parseTimekeepingPeriodEnd(p), period: p }))
    .filter(({ end }) => end !== undefined);
  if (validPeriods.length === 0) {
    return undefined;
  } else {
    return validPeriods.reduce((max, curr) => {
      if (max.end!.getTime() > curr.end!.getTime()) {
        return max;
      } else {
        return curr;
      }
    }).period;
  }
}
